import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FeedbackSentimentEnum, LessonSkillsEnum } from 'types/generated/client';
import { UploadProvider } from 'context/UploadVideoContext';
import Stepper from 'screens/TournamentsBuilder/components/Stepper';
import Modal from 'components/modals/Modal';
import { Step1 } from './CoachSteps';
import FinishModal from './CoachSteps/FinishStep';
import PlayerAvatar from './PlayerAvatar';
import ReflectionNote from './ReflectNote';
import ModalHeader from './ReflectionModalHeader';
import { ReflectionFlowModalProps, ReflectionFormType } from './props';
import { reflecionFormDefaultValues, reflectionFormSchema } from './utils';

const ReflectionFlowModal: React.FC<ReflectionFlowModalProps> = ({
  isOpen,
  handleClose,
  selectedPlayerForReflection,
  handleAddReflection,
  submitting,
  initialValues,
  handleEditReflection,
  activeLessonParticipantsCount,
  activeLessonPendingReflectionsCount,
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(1);
  const [reflectOnAllParticipants, setReflectOnAllParticipants] = useState<boolean>(true);

  const reflectionForm = useForm<ReflectionFormType>({
    resolver: zodResolver(reflectionFormSchema),
    defaultValues: reflecionFormDefaultValues,
  });

  const isEditMode = !!initialValues;

  const showReflectionNote = isEditMode
    ? activeLessonParticipantsCount - activeLessonPendingReflectionsCount > 1
    : activeLessonPendingReflectionsCount > 1;

  const closeModal = () => {
    handleClose();
    setTimeout(() => {
      setActiveStepIndex(1);
      reflectionForm.reset(reflecionFormDefaultValues);
    }, 300); // for closing animation
  };

  const handleSubmit = async () => {
    try {
      const values = reflectionForm.getValues();

      if (isEditMode) {
        await handleEditReflection?.(values, reflectOnAllParticipants);
        closeModal();
      } else {
        await handleAddReflection(values, reflectOnAllParticipants);
        setActiveStepIndex(2);
      }
    } catch (err) {
      console.error('Step Change Error:', err);
    }
    return;
  };

  useEffect(() => {
    if (initialValues) {
      reflectionForm.reset(initialValues);
    }
  }, [initialValues]);

  return (
    <Modal
      isOpen={isOpen}
      handleClose={closeModal}
      classNameMaxWidth="max-w-[40rem] overflow-visible"
    >
      <ModalHeader title={'Lesson Recap'} handleClose={closeModal} />
      {activeStepIndex === 1 && (
        <div className="px-8">
          <Stepper
            className="max-w-full"
            currentStep={activeStepIndex - 1}
            steps={[
              { title: '', description: '' },
              { title: '', description: '' },
            ]}
          />

          <PlayerAvatar selectedPlayer={selectedPlayerForReflection} />

          {showReflectionNote && (
            <ReflectionNote
              reflectOnAll={reflectOnAllParticipants}
              setReflectOnAll={setReflectOnAllParticipants}
              isEditMode={isEditMode}
            />
          )}

          <hr className="my-8" />
        </div>
      )}
      {/* can be used later  */}
      {/* <CoachPrompt
        selectedPlayer={selectedPlayerForReflection}
        visible={activeStepIndex === 0}
        onChangeStep={handleChangeStep}
        handleClose={handleClose}
      /> */}
      <UploadProvider>
        <Step1
          visible={activeStepIndex === 1}
          control={reflectionForm.control}
          trigger={reflectionForm.trigger}
          getValues={reflectionForm.getValues}
          setValue={reflectionForm.setValue}
          clearErrors={reflectionForm.clearErrors}
          setError={reflectionForm.setError}
          onChangeStep={handleSubmit}
          submitting={submitting}
        />
      </UploadProvider>
      {/* <Step2
        visible={activeStepIndex === 2}
        control={reflectionForm.control}
        skillsFeedback={skillsFeedback || []}
        trigger={reflectionForm.trigger}
        onChangeStep={handleChangeStep}
        clearErrors={reflectionForm.clearErrors}
        setError={reflectionForm.setError}
        submitting={submitting}
        errors={reflectionForm.formState.errors}
      /> */}
      <FinishModal
        visible={activeStepIndex === 2}
        playerName={selectedPlayerForReflection?.fullName as string}
        onFinish={() => closeModal()}
      />
    </Modal>
  );
};

export default ReflectionFlowModal;
